import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, Zap } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPrice, shopifyCheckoutUrl, SHOPIFY_DOMAIN } from "@/lib/api";

/**
 * Editorial product card — spotlight photography, clipped corner ticks,
 * inline size/color selection, big ADD TO CART + BUY NOW targets.
 */
export default function ProductCard({ product, index = 0 }) {
  const { addItem, setOpen } = useCart();
  const [size, setSize] = useState(product.sizes?.[0] || "OS");
  const [color, setColor] = useState(product.colors?.[0]?.name || "Default");
  const [imgIdx, setImgIdx] = useState(0);

  const image = product.images?.[imgIdx] || product.images?.[0];
  const variantId = product.shopify_variant_ids?.[size] || null;

  const buildItem = () => ({
    productId: product.id,
    name: product.name,
    price: product.price,
    image: image?.url,
    size,
    color,
    qty: 1,
    variantId,
    shopifyProductUrl: product.shopify_product_url || "",
  });

  const handleAdd = () => {
    addItem(buildItem());
  };

  const handleBuyNow = () => {
    const item = buildItem();
    addItem(item);
    const url = shopifyCheckoutUrl([item]);
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
      setOpen(false);
    } else if (product.shopify_product_url) {
      window.open(product.shopify_product_url, "_blank", "noopener,noreferrer");
    } else {
      setOpen(true);
      toast.info("Real checkout activates once the Shopify store is linked.", {
        description: SHOPIFY_DOMAIN
          ? "Some products still need their Shopify variant IDs."
          : "Set REACT_APP_SHOPIFY_DOMAIN in settings — then every BUY NOW goes straight to Shopify's secure checkout (Apple Pay, Shop Pay, cards, supported crypto).",
      });
    }
  };

  return (
    <article data-testid={`product-card-${product.id}`} className="group relative flex flex-col h-full bg-[#0F0F14] border border-[#1A1A22] hover:border-[#F26522]/60 transition-colors duration-500">
      {/* corner ticks */}
      <span aria-hidden="true" className="absolute top-0 left-0 w-5 h-5 border-t-2 border-l-2 border-[#F26522] opacity-0 group-hover:opacity-100 transition-opacity z-10" />
      <span aria-hidden="true" className="absolute bottom-0 right-0 w-5 h-5 border-b-2 border-r-2 border-[#F26522] opacity-0 group-hover:opacity-100 transition-opacity z-10" />

      <div className={`relative overflow-hidden aspect-[4/5] ${product.image_fit === "contain" ? "bg-[#f2f2f2]" : "bg-[#121218]"}`}>
        <button
          className="absolute inset-0 w-full h-full"
          onClick={() => {
            if (product.shopify_product_url) {
              window.open(product.shopify_product_url, "_blank", "noopener,noreferrer");
              return;
            }
            setImgIdx((i) => (i + 1) % (product.images?.length || 1));
          }}
          aria-label={product.shopify_product_url ? `View ${product.name} on Shopify` : `Next photo of ${product.name}`}
        >
          <img
            src={image?.url}
            alt={image?.alt || product.name}
            loading="lazy"
            decoding="async"
            className={
              product.image_fit === "contain"
                ? "w-full h-full object-contain"
                : "w-full h-full object-cover saturate-[0.9] contrast-[1.06] group-hover:scale-[1.04] transition-transform duration-700"
            }
          />
        </button>
        {product.images?.length > 1 && (
          <div className="absolute bottom-3 left-3 flex gap-1.5" aria-hidden="true">
            {product.images.map((_, i) => (
              <span key={i} className={`w-6 h-1 ${i === imgIdx ? "bg-[#F26522]" : "bg-white/40"}`} />
            ))}
          </div>
        )}
        {product.badge && (
          <span className="absolute top-3 left-3 bg-[#08080A]/85 backdrop-blur px-3 py-1.5 font-mono text-[11px] font-semibold tracking-[0.2em] text-[#F26522] uppercase border border-[#F26522]/40">
            {product.badge}
          </span>
        )}
        {product.is_placeholder && (
          <span className="absolute top-3 right-3 bg-[#08080A]/85 backdrop-blur px-3 py-1.5 font-mono text-[11px] tracking-[0.15em] text-zinc-300 uppercase border border-[#2A2A36]">
            Sample visual
          </span>
        )}
      </div>

      <div className="flex flex-col flex-1 p-5 sm:p-6 gap-3">
        <div className="flex items-start justify-between gap-4">
          <h3 className="font-display font-extrabold uppercase text-lg text-white leading-tight">{product.name}</h3>
          <p data-testid={`price-${product.id}`} className="font-mono font-semibold text-lg text-[#F26522] shrink-0">
            {formatPrice(product.price, product.currency)}
          </p>
        </div>
        <p className="text-zinc-300 text-sm leading-relaxed">{product.description}</p>

        {product.related_release_id && (
          <Link
            to="/music"
            className="font-mono text-xs tracking-[0.2em] uppercase text-[#F5E6D3] hover:text-[#F26522] transition-colors"
          >
            Soundtrack in Music →
          </Link>
        )}

        <div className="mt-auto pt-2 flex flex-col gap-4">
          <div>
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-zinc-300 mb-2">Size — {size}</p>
            <div className="flex flex-wrap gap-2">
              {product.sizes?.map((s) => (
                <button
                  key={s}
                  data-testid={`size-${product.id}-${s}`}
                  onClick={() => setSize(s)}
                  aria-pressed={size === s}
                  className={`min-w-[52px] h-[52px] px-3 font-display font-bold text-sm border transition-colors ${
                    size === s
                      ? "bg-[#F26522] text-black border-[#F26522]"
                      : "border-[#2A2A36] text-white hover:border-[#F26522]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-zinc-300 mb-2">Color — {color}</p>
            <div className="flex gap-3">
              {product.colors?.map((c) => (
                <button
                  key={c.name}
                  data-testid={`color-${product.id}-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setColor(c.name)}
                  aria-pressed={color === c.name}
                  aria-label={`Color ${c.name}`}
                  title={c.name}
                  className={`w-[44px] h-[44px] rounded-full border-2 transition-all ${
                    color === c.name ? "border-[#F26522] scale-110" : "border-[#2A2A36] hover:border-zinc-400"
                  }`}
                  style={{ background: c.hex }}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              data-testid={`add-to-cart-${product.id}`}
              onClick={handleAdd}
              className="h-14 bg-[#F26522] hover:bg-[#FF7733] text-black font-display font-extrabold uppercase tracking-widest text-sm flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 active:scale-95"
            >
              <ShoppingBag className="w-4 h-4" aria-hidden="true" />
              Add to Cart
            </button>
            <button
              data-testid={`buy-now-${product.id}`}
              onClick={handleBuyNow}
              className="h-14 border-2 border-white/80 hover:border-[#F26522] hover:text-[#F26522] text-white font-display font-extrabold uppercase tracking-widest text-sm flex items-center justify-center gap-2 transition-colors active:scale-95"
            >
              <Zap className="w-4 h-4" aria-hidden="true" />
              Buy Now
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
